import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Swords, Flag, Users, Megaphone, LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Endpoint + row-mapper per kind. Each kind has a matching chat channel.
const KIND_CFG = {
  arena: {
    label: "Open Arena challenges", icon: Swords, endpoint: "/arena/battles/open",
    parse: (d) => [
      ...((d?.mine || []).map((b) => ({ id: b.id, mine: true, to: "/arena", title: b.challenger_name, sub: `${b.wager}c wager · PWR ${b.challenger_power}` }))),
      ...((d?.open || []).map((b) => ({ id: b.id, mine: false, to: "/arena", title: b.challenger_name, sub: `${b.wager}c wager · PWR ${b.challenger_power}` }))),
    ],
  },
  race: {
    label: "Open Gauntlet races", icon: Flag, endpoint: "/race/open",
    parse: (d) => (d || []).map((r) => ({ id: r.id, mine: r.is_host, to: `/race/${r.id}`, title: `${r.host_user_id ? "Gauntlet" : "Race"} lobby`, sub: `${r.open_spots} of ${r.slots} seats open` })),
  },
  cavern: {
    label: "Open Cavern runs", icon: Users, endpoint: "/cavern/open",
    parse: (d) => (d || []).map((c) => ({ id: c.id, mine: c.is_host, to: `/cavern/${c.id}`, title: c.foe?.name || "Cavern run", sub: `${c.open_spots} of ${c.slots} seats open` })),
  },
};

// A live "looking for players" board for one kind, shown inside its chat tab.
export function OpenMatches({ kind }) {
  const cfg = KIND_CFG[kind];
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ["lfg", kind],
    queryFn: async () => (await api.get(cfg.endpoint)).data,
    refetchInterval: 5000,
    enabled: !!cfg && !!user,
  });
  const rows = useMemo(() => (cfg ? cfg.parse(data) : []), [cfg, data]);

  const broadcast = useMutation({
    mutationFn: async () => (await api.post("/lfg/broadcast", { kind })).data,
    onSuccess: () => { toast.success("Call for players sent to chat!"); qc.invalidateQueries({ queryKey: ["chat", kind] }); },
    onError: (e) => toast.error(apiError(e, "Couldn't broadcast")),
  });

  const myOpen = rows.some((r) => r.mine);
  const Icon = cfg?.icon || Swords;
  if (!cfg) return null;

  return (
    <div className="mb-2 rounded-lg border border-cyan-400/20 bg-cyan-500/[0.05] p-2" data-testid={`open-matches-${kind}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-3.5 w-3.5 text-cyan-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-200/80 flex-1">{cfg.label}</span>
        <Button size="sm" data-testid={`lfg-broadcast-${kind}`} disabled={!myOpen || broadcast.isPending}
          onClick={() => broadcast.mutate()}
          className="h-6 px-2 bg-cyan-400 text-black hover:bg-cyan-300 text-[11px] disabled:opacity-40"
          title={myOpen ? "Call for players in chat" : "Open a match first to broadcast"}>
          {broadcast.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Megaphone className="h-3 w-3 mr-1" /> Broadcast</>}
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-white/40 text-xs font-mono text-center py-2">Nothing open right now. Open one and broadcast it!</div>
      ) : (
        <div className="space-y-1.5">
          {rows.slice(0, 6).map((r) => (
            <div key={r.id} data-testid={`lfg-row-${r.id}`} className="flex items-center gap-2 rounded-md border border-white/10 bg-black/30 px-2 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="text-white text-xs font-medium truncate flex items-center gap-1.5">
                  {r.mine && <span className="text-[8px] font-mono bg-violet-400 text-black px-1 rounded">YOURS</span>}
                  {r.title}
                </div>
                <div className="font-mono text-[10px] text-white/45 truncate">{r.sub}</div>
              </div>
              <Button size="sm" data-testid={`lfg-join-${r.id}`} onClick={() => navigate(r.to)}
                className="h-6 px-2 bg-white/10 hover:bg-white/20 text-white text-[11px]">
                <LogIn className="h-3 w-3 mr-1" /> {r.mine ? "View" : "Join"}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
